import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PaginatedMuiTable, Fetcher, Column } from '@lendica/components';

import {
    DealStatus,
    DealDisplayStatus,
    currencyFormatter,
    percentageFormatter,
    dateFormatter,
    formatProductName,
    invoiceOrDealStatusToLabel,
} from '@lendica/utils';

import { StatusChip } from '@lendica/components';
import { useWalletApi } from '../walletAPI';

type DealRow = {
    id: string;
    invoice_number?: string | null;
    funded_date: string;
    product: string;
    amount: number;
    paid: number; // 0..1
    deal_status: DealStatus | DealDisplayStatus; // enum values used for label mapping
};

const columns: Column<DealRow>[] = [
    {
        id: 'product',
        label: 'Product',
        formatter: formatProductName,
        tableCellProps: {
            padding: 'none',
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'funded_date',
        label: 'Date',
        CellContent: ({ value }) => {
            return <>{dateFormatter(value, false)}</>;
        },
        tableCellProps: {
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'amount',
        label: 'Amount',
        formatter: (val: number) => currencyFormatter(val),
        tableCellProps: {
            align: 'right',
            padding: 'none',
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'paid',
        label: 'Paid',
        formatter: (val: number) => percentageFormatter(val, 0),
        tableCellProps: {
            align: 'right',
            style: {
                fontSize: '13px',
            },
        },
    },
    {
        id: 'deal_status',
        label: 'Status',
        CellContent: ({ value }) => {
            return (
                <StatusChip
                    status={
                        invoiceOrDealStatusToLabel[value as keyof typeof invoiceOrDealStatusToLabel]
                    }
                    sx={{
                        '& .MuiChip-label': {
                            fontSize: '12.5px',
                            maxWidth: '120px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        },
                    }}
                    deleteIcon={<></>}
                />
            );
        },
        tableCellProps: {
            width: 120,
            align: 'left',
            padding: 'none',
            style: {
                fontSize: '13px',
            },
        },
    },
];

export const PaginatedDealsTable = () => {
    const navigate = useNavigate();
    const api = useWalletApi();

    const fetchDeals = React.useCallback<Fetcher<DealRow>>(
        async ({ page, pageSize }) => {
            const { count, next, previous, results } = await api.getDeals(page, pageSize);
            const rows: DealRow[] = results.map(
                ({
                    id,
                    invoice_number,
                    funded_date,
                    product_name,
                    total_collected,
                    payback_amount,
                    deal_status,
                    gross_funded_amount,
                    status,
                }) => ({
                    id,
                    invoice_number,
                    funded_date,
                    product: product_name,
                    amount: gross_funded_amount,
                    paid: payback_amount ? total_collected / payback_amount : 0,
                    deal_status: status === DealDisplayStatus.Rejected ? status : deal_status,
                })
            );
            return { count, next, previous, results: rows };
        },
        [api]
    );

    const handleTableRowClick = (row: any) => {
        navigate(`deal/${row.id}`);
    };

    return (
        <PaginatedMuiTable<DealRow>
            fetcher={fetchDeals}
            columns={columns}
            getRowKey={r => r.id}
            initialPageSize={5}
            pageSizeOptions={[5, 10, 15]}
            syncUrlParams
            onRowClick={handleTableRowClick}
            renderLoading={false}
        />
    );
};
